// Global Instructions Rule Applied!

import React from 'react'
import { Tag, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import type { AgentProvenanceEntry } from '../../state_manager/data_models/agent_provenance_entry.ts'

const DATE_FORMAT = 'MM/DD/YYYY'

export type RecordProvenanceSurface = 'default' | 'onBlue'

interface RecordProvenanceTagProps {
  /**
   * The record's `<table>__created_by_name`, resolved by the RPC through
   * `audit.resolve_user_name`. That helper returns 'System' for a NULL
   * created_by — the state ~93% of migrated deals are in — so 'System' is a
   * real value to render, not a gap to hide.
   */
  createdByName?: string | null
  /** The record's `<table>__date_created`. Date only, per owner 2026-09-16. */
  dateCreated?: string | null
  /**
   * When the record was created by an agent, `AgentProvenanceTag` already
   * states who made it and on whose behalf. Pass the same
   * `prog__created_provenance` here and this component renders nothing, so the
   * two markers never sit side by side saying overlapping things. Callers drop
   * both in unconditionally and the right one wins.
   */
  agentProvenance?: AgentProvenanceEntry | null
  /**
   * Which ground the tag sits on. 'default' is the house blue Tag for a white
   * surface (the full form's tab bar). 'onBlue' is the outlined white
   * treatment for the drawer's blue gradient banner, where the blue Tag has no
   * contrast — owner-approved 2026-09-16, recorded in FRONTEND_DESIGN_PATTERNS.
   */
  surface?: RecordProvenanceSurface
  /** Cap before the label ellipsises; the Tooltip always has it in full. */
  maxWidth?: number
}

/**
 * RecordProvenanceTag — "Leif Danielsen · 07/29/2026".
 *
 * The human half of record provenance, and the sibling of
 * `AgentProvenanceTag`: that one marks the records an agent wrote, this one
 * answers the same question ("where did this record come from?") for the
 * records people and migrations wrote. They share a slot, and the agent tag
 * wins when both could apply.
 *
 * System-wide by design (owner 2026-09-16): the slot lives in the shared
 * record chrome — `RightMenu`'s drawer banner for every module's drawer, and
 * the full form's tab bar — so an entity surfaces provenance by projecting
 * `date_created` + `created_by_name` from its detail RPC and passing them in.
 * Deals are wired; companies, contacts and fundraising need only the two RPC
 * columns and the prop.
 *
 * Reference information, not a status — hence blue on a white ground, per the
 * house Tag vocabulary. Dates render MM/DD/YYYY, also per house rule.
 */
export function RecordProvenanceTag({
  createdByName,
  dateCreated,
  agentProvenance,
  surface = 'default',
  maxWidth = 260
}: RecordProvenanceTagProps): React.ReactElement | null {
  // The agent marker is the fuller story; let it speak alone.
  if (agentProvenance) return null

  const created = dateCreated ? dayjs(dateCreated) : null
  const onDate = created?.isValid() ? created.format(DATE_FORMAT) : null

  // Nothing to say — a record whose RPC does not project these yet, or a grid
  // read where created_by_name is deliberately NULL.
  if (!createdByName && !onDate) return null

  const label = [createdByName, onDate].filter(Boolean).join(' · ')
  const sentence = [
    createdByName ? `Created by ${createdByName}` : 'Created',
    onDate ? ` on ${onDate}` : ''
  ].join('')

  const onBlue = surface === 'onBlue'

  return (
    <Tooltip title={sentence}>
      <Tag
        color={onBlue ? undefined : 'blue'}
        data-debug='record-provenance-tag'
        data-surface={surface}
        className='m-0 text-xs inline-flex items-center gap-1 max-w-full overflow-hidden'
        style={
          onBlue
            ? {
                maxWidth,
                background: 'transparent',
                borderColor: 'rgba(255,255,255,0.55)',
                color: '#FFFFFF'
              }
            : { maxWidth }
        }
      >
        <FontAwesomeIcon icon={faUser} />
        <span className='truncate'>{label}</span>
      </Tag>
    </Tooltip>
  )
}

export default RecordProvenanceTag
